// Every asset the game can load, keyed the way scenes reference textures/sounds, and
// what each scene needs from it (Tasks 3-4). Pure — no Phaser — so `node --test` covers it.
import { SFX_KEYS, MUSIC_KEY } from './audio/eventMap.js';
import { JUNCTION_PAIRS, junctionKey, SEGMENT_IDS } from './scenes/lessonScript.mjs';
import { STREET_LOOKS, lookFor, streetTileKeys, streetPrefetch } from './street/streetLooks.mjs';
import { YARD_LOOKS, yardKey, yardFarKey, yardRects, yardPrefetch } from './scenes/yardLooks.mjs';
import { PROP_KINDS } from './farm/lawnProps.mjs';
import { podworkoState } from './farm/podworkoPlot.mjs';
import { sheetOnly } from './ui/clothSlots.mjs';
import { driverKey, driverPictures } from './look/hairArt.mjs';

// The crops' real sprites (frontend/public/assets/farm/farm_crop_<id>_<stage>.webp,
// FarmWorld._paintCrop) — a cluster of plants at each growth stage. Every stage of every crop
// is a manifest key (23 crops, in grow-time order, × cropStageKey's three stages), but
// `farmCropKeys` below decides which of those actually block a scene — only what's planted.
const FARM_CROP_IDS = ['rzodkiewka', 'salata', 'truskawka', 'cukinia', 'stokrotka', 'marchewka', 'bratek', 'pomidor', 'ogorek',
  'malina', 'papryka', 'tulipan', 'ziemniak', 'mak', 'pszenica', 'dynia', 'lawenda', 'kukurydza', 'arbuz', 'slonecznik', 'zyto',
  'roza', 'storczyk'];
// Flower colours (Kwiaty §7): the non-start ones, farm_crop_<id>_<colour>_<stage>.webp.
const FARM_CROP_COLORS = { tulipan: ['zolty', 'rozowy', 'fioletowy'], roza: ['bialy', 'zolty', 'czarny'],
  storczyk: ['fioletowy', 'zolty', 'niebieski'], bratek: ['zolty', 'niebieski', 'czerwonopomaranczowy'] };
const FARM_CROP_STAGES = ['seedling', 'growing', 'ready'];
// Trees (sub-project 4b, scripts/farm_trees.py): six trees × three looks.
const FARM_TREE_IDS = ['jablon', 'wisnia', 'sliwa', 'grusza', 'brzoskwinia', 'orzech'];
const FARM_TREE_STAGES = ['sapling', 'grown', 'ripe'];
// Vehicle sprites (farm/FarmVehicle.js, scripts/farm_vehicles.py): 3 tools × 2 sizes.
const FARM_VEHICLE_KEYS = ['tractor', 'seeder', 'harvester'].flatMap((t) => [2, 4].map((n) => `farm_vehicle_${t}_${n}`));
// The Konewka (same script, 2026-10-27): the Rynek card on your farm, the tool tile on a friend's.
const FARM_KONEWKA = 'farm_tool_konewka';
// The watering splash (scripts/farm_fx.py, farm/farmFxArt.js): a friend's help, on their farm and on the host's.
const FARM_SPLASH = 'farm_fx_splash';
// Farm objects (sub-project 4a, scripts/farm_objects_art.py): decorations and landmarks by id,
// hay, the plaque board, and one fence post + one rail (mirrored in code for the other axis).
const FARM_OBJECT_IDS = ['roslina', 'balony', 'girlanda', 'skrzynka_kwiaty', 'latarnia', 'posag', 'lawka', 'fontanna',
  'stodola', 'wiatrak', 'mlyn', 'szklarnia', 'willa', 'siano', 'plaque', 'fence_post', 'fence_rail', 'kurnik', 'obora'];
// Farm animals (4c, scripts/farm_animals_art.py): a 4-frame walk + an idle pose each. A species is
// listed once its art exists (the manifest test checks every key is a real file).
const FARM_ANIMAL_IDS = ['kura', 'krowa'];
const FARM_ANIMAL_POSES = ['walk0', 'walk1', 'walk2', 'walk3', 'idle', 'stand'];
// The scenery outside the fence (farm/lawnProps.mjs): trees, shrubs, flowers, stones.
const FARM_PROP_KINDS = PROP_KINDS;
// The farm backdrop (farm/FarmBackdrop.js, scripts/farm_backdrop.py): terrain strips, band
// objects, clouds, sun/moon, birds and horizon tufts. It all blocks the farm like the rest of
// its scenery; the hall prefetches its FarmScene neighbour, so it is usually warm already.
const FARM_BACKDROP = [
  'farm_bd_far', 'farm_bd_mid', 'farm_bd_near',
  'farm_bd_barn', 'farm_bd_farmhouse', 'farm_bd_silo', 'farm_bd_windmill', 'farm_bd_blades',
  ...[0, 1, 2, 3].map((i) => `farm_bd_tree_${i}`),
  ...[0, 1, 2, 3, 4].map((i) => `farm_bd_cloud_${i}`),
  'farm_bd_sun', 'farm_bd_moon', 'farm_bd_bird_0', 'farm_bd_bird_1',
  ...[0, 1, 2, 3].map((i) => `farm_bd_tuft_${i}`),
];

// The garden's cars (scripts/farm_cars.py): every model, painted in the farm's style.
const FARM_CAR_MODELS = ['panda', 'bclass', 'q5', 't03', 'model3', 'c8', 'yaris_l'];
const IMAGE_FOLDERS = {
  characters: [
    'natalka', 'natalka_base', 'ken_base', 'npc_instructor', 'npc_mechanic', 'npc_child',
    'npc_child_side', 'npc_child_walk', 'npc_child_walk_right',
    // Head-and-shoulders of each playable character, seen from the side, drawn behind the
    // car's see-through window while driving (carSprite addDriver); each hairstyle with its own
    // (look/hairArt driverPictures, from HAIR_ART).
    'driver_natalka', 'driver_ken', ...driverPictures(),
  ],
  scenes: [
    'bg_home', 'bg_home_lvl2', 'bg_home_lvl3', 'bg_home_lvl4', 'bg_home_lvl5',
    'bg_shop',
    // Only the hall's door window draws these now — the garden itself is retired.
    'bg_garden_lvl3_morning', 'bg_garden_lvl3_day', 'bg_garden_lvl3_evening', 'bg_garden_lvl3_night',
    'bg_kitchen', 'bg_salon', 'bg_second_floor',
    ...STREET_LOOKS.flatMap(streetTileKeys), 'bg_furniture_shop', 'bg_pet_shop', 'bg_clothing_shop',
    'bg_bike_shop', 'bg_bike_shop_0', 'bg_bike_shop_1',
    'bg_game_hall_down', 'bg_game_hall_up', 'bg_board_game',
    'bg_driving_school', 'bg_car_dealer',
    // The practical-lesson road segments (bg_seg_*) are appended below and the junction
    // patches live in scenes/junctions — they ARE in the manifest (TrainingRouteScene needs
    // to be able to load them by key), but ~7.5 MB that only a driving lesson looks at, so
    // `tiers()` never prefetches them: they arrive on demand in TrainingRouteScene.preload(),
    // whose needsFor() gets the chosen route through `ctx.route`.
    // The front yard and the Podwórko, their painted sky cut out (scripts/yard_sky.py): the live sky is drawn behind
    // them. The painted-sky bg_main_* / bg_backyard_* files stay on disk a release for tabs running the old code.
    ...['front', 'back'].flatMap((side) => YARD_LOOKS.flatMap((look) => [1, 2, 3, 4, 5].map((n) => yardKey(side, look, n)))),
    ...YARD_LOOKS.map(yardFarKey),   // the Podwórko's far landscape, one per look (scripts/yard_far.py)
  ],
  icons: ['icon_home', 'icon_back', 'icon_shop', 'icon_outside', 'icon_lock', 'icon_hungry', 'icon_pet_heart'],
  items: [
    'plot_empty',   // placement fallback texture (the farm's own art is generated at runtime)
    'furniture_kanapa', 'furniture_lampa', 'furniture_zegar', 'furniture_zegar_hand', 'furniture_obraz',
    'furniture_szafa', 'furniture_lozko',
    // sofas and beds (2026-10-29): seven designs each; a bed's duvet is drawn again over whoever lies in it
    ...['_2', '_3', '_4', '_5', '_6', '_7'].map((s) => `furniture_kanapa${s}`),
    ...['', '_2', '_3', '_4', '_5', '_6', '_7'].flatMap((s) => [`furniture_lozko${s}`, `furniture_lozko${s}_front`]).filter((k) => k !== 'furniture_lozko'),
    'bike_pink', 'bike_blue', 'bike_grease',
    'buda_kurnik', 'buda_obora', 'buda_buda', 'buda_kocie',
    'animal_kura', 'animal_krowa', 'animal_pies', 'animal_kot',
    'product_jajko', 'product_mleko', 'egg_1', 'egg_2',
    'eggs_2', 'eggs_3', 'eggs_4', 'eggs_5', 'milk_2', 'milk_3', 'milk_4', 'milk_5',
    'food_ziarno', 'food_siano', 'food_mieso',
    'game_checkers', 'game_chess', 'game_go', 'game_mancala', 'game_makao', 'game_backgammon',
    'checkers_board', 'checkers_piece_red', 'checkers_piece_brown',
    'checkers_queen_red', 'checkers_queen_brown', 'ranking_board',
    'mancala_board', 'mancala_stone_0', 'mancala_stone_1', 'mancala_stone_2', 'mancala_stone_3',
    'backgammon_board', 'backgammon_checker_w', 'backgammon_checker_b',
    'backgammon_die_1', 'backgammon_die_2', 'backgammon_die_3',
    'backgammon_die_4', 'backgammon_die_5', 'backgammon_die_6',
    // Theory-quiz illustrations (one per question that has art — see backend questions.py).
    'quiz_q02', 'quiz_q03', 'quiz_q05', 'quiz_q07', 'quiz_q10', 'quiz_q12', 'quiz_q13', 'quiz_q17',
    'quiz_q18', 'quiz_q19', 'quiz_q21', 'quiz_q22', 'quiz_q25', 'quiz_q26', 'quiz_q27',
    'quiz_q33', 'quiz_q34', 'quiz_q35', 'quiz_q36', 'quiz_q37',
    // Cars — `side` drives/parks on the street, `front` is the dealer/garage portrait
    // that carries the licence plate (see CarSprite.js). Keys mirror backend catalog.CARS.
    'car_panda_side', 'car_panda_front', 'car_bclass_side', 'car_bclass_front',
    'car_q5_side', 'car_q5_front', 'car_t03_side', 'car_t03_front',
    'car_model3_side', 'car_model3_front', 'car_c8_side', 'car_c8_front',
    'car_yaris_l_side', 'car_yaris_l_front',
    // Practical-lesson v2 traffic light (Task 6) — the sign posts themselves are now baked
    // into each bg_seg_* segment background rather than loaded as separate item sprites.
    'traffic_light_red', 'traffic_light_redyellow', 'traffic_light_green',
    // Route signs the scene places itself, right by what they announce (their painted
    // twins were erased from the segment halves): see segmentFeatures.mjs / TrainingRouteScene.
    'sign_yield', 'sign_stop', 'sign_crossing',
  ],
  clothes: [
    'cloth_bluzka_red', 'cloth_bluzka_blue', 'cloth_bluzka_green', 'cloth_bluzka_stripes',
    'cloth_spodniczka_pink', 'cloth_spodniczka_jeans', 'cloth_spodniczka_black', 'cloth_spodniczka_flowers',
    'cloth_buty_sport', 'cloth_buty_balet', 'cloth_buty_kozaki', 'cloth_buty_sandaly',
    'cloth_akc_kokarda', 'cloth_akc_kapelusz', 'cloth_akc_okulary', 'cloth_akc_korona',
    'cloth_akc_korona_back',  // crown rim drawn behind the head (see NatalkaSprite accessory_back)
    'cloth_akc_kokarda_back', // bow parts that fall on the face/ear — drawn behind the head, rest stays on the hair
    // Ken's wardrobe (2026-10-01): shop and Garderoba pictures only — on his body he wears the walk sheets
    'cloth_ken_tee_brown', 'cloth_ken_tee_logo', 'cloth_ken_hoodie', 'cloth_ken_jacket_plaid',
    'cloth_ken_shorts_denim', 'cloth_ken_jeans_black', 'cloth_ken_cargo', 'cloth_ken_joggers',
    'cloth_ken_shoes_runner', 'cloth_ken_shoes_white', 'cloth_ken_shoes_boots', 'cloth_ken_shoes_japonki',
    'cloth_ken_akc_cap', 'cloth_ken_akc_beanie', 'cloth_ken_akc_sunglasses', 'cloth_ken_akc_headphones',
    // Natalka's second wardrobe, batch 1 (2026-10-05): shop and Garderoba pictures only, like Ken's
    'cloth_nat_jeans_wide', 'cloth_nat_corduroy', 'cloth_nat_parachute', 'cloth_nat_patchwork',
    'cloth_nat_flares_lavender', 'cloth_nat_joggers_light', 'cloth_nat_sweater_band', 'cloth_nat_wellies', 'cloth_nat_slipons_check', 'cloth_nat_basket_shoes',
    'cloth_nat_headphones', 'cloth_nat_bucket_hat', 'cloth_nat_headband', 'cloth_nat_jacket_denim', 'cloth_nat_vest_puffer', 'cloth_nat_jersey_basket',
    'cloth_nat_fleece_brown', 'cloth_nat_tee_blue_word', 'cloth_nat_tee_wave',
  ],
  decorations: [
    'decoration_roslina', 'decoration_balony', 'decoration_girlanda',
    'decoration_dywan', 'decoration_plakat', 'decoration_zyrandol',
    'decoration_lawka', 'decoration_skrzynka_kwiaty', 'decoration_latarnia',
    'decoration_posag', 'decoration_fontanna',
  ],
  'scenes/junctions': JUNCTION_PAIRS.map(([a, b]) => junctionKey(a, b)),
  farm: ['farm_lawn', 'farm_tile_plowed', 'farm_tile_fallow', 'farm_tile_path',
    // Scenery (scripts/farm_scenery.py): the picket fence around the owned land with its
    // gate and corner posts, and the props scattered over the lawn.
    'farm_fence_se', 'farm_fence_sw', 'farm_gate', 'farm_fence_post',
    ...FARM_PROP_KINDS.map((kind) => `farm_prop_${kind}`),
    // The player's house at the end of the path, one per house level (scripts/farm_house.py).
    ...[1, 2, 3, 4, 5].map((n) => `farm_house_lvl${n}`),
    // Podwórko beside the house when unlocked, one per Podwórko level (scripts/farm_podworko.py).
    ...[1, 2, 3, 4, 5].map((n) => `farm_podworko_lvl${n}`),
    ...FARM_CAR_MODELS.map((m) => `farm_car_${m}`),
    ...FARM_BACKDROP,
    ...FARM_CROP_IDS.flatMap((id) => FARM_CROP_STAGES.map((stage) => `farm_crop_${id}_${stage}`)),
    ...Object.entries(FARM_CROP_COLORS).flatMap(([id, cs]) => cs.flatMap((c) => FARM_CROP_STAGES.map((stage) => `farm_crop_${id}_${c}_${stage}`))),
    ...FARM_VEHICLE_KEYS, FARM_KONEWKA, FARM_SPLASH,
    ...FARM_OBJECT_IDS.map((id) => `farm_obj_${id}`),
    ...FARM_TREE_IDS.flatMap((id) => FARM_TREE_STAGES.map((stage) => `farm_tree_${id}_${stage}`)),
    ...FARM_ANIMAL_IDS.flatMap((id) => FARM_ANIMAL_POSES.map((pose) => `farm_animal_${id}_${pose}`))],
};
// The route segments live in scenes/ too.
IMAGE_FOLDERS.scenes.push(...SEGMENT_IDS.map((id) => `bg_seg_${id}`));

// Manifest ids: an image's id is its texture key; a sound's id is `audio:<key>` because four
// stems (animal_kot…) are both a sprite and a sound (Phaser keeps them in separate caches).
// `key` is what the loader registers, `path` the part after /assets/.
export const audioId = (key) => `audio:${key}`;

export const ASSETS = {};
for (const [folder, keys] of Object.entries(IMAGE_FOLDERS)) {
  for (const key of keys) ASSETS[key] = { kind: 'image', key, path: `${folder}/${key}.webp` };
}
for (const key of SFX_KEYS) ASSETS[audioId(key)] = { kind: 'audio', key, path: `audio/sfx/${key}.mp3` };
ASSETS[audioId(MUSIC_KEY)] = { kind: 'audio', key: MUSIC_KEY, path: `audio/music/${MUSIC_KEY}.mp3` };

export const ALL_KEYS = Object.keys(ASSETS);
export const assetPath = (id) => ASSETS[id]?.path ?? null;

import { gardenBgKey, GARDEN_BG_BY_PHASE } from './render/gardenWindow.mjs';

const exists = (key) => key in ASSETS;   // the manifest IS the "does this texture exist" oracle
const keysWith = (prefix) => ALL_KEYS.filter((k) => k.startsWith(prefix));
const CAR_MODELS = ['panda', 'bclass', 'q5', 't03', 'model3', 'c8', 'yaris_l'];

// Peer bodies/clothes (RemoteAvatar/NatalkaSprite for the player next to you) are tier 1 with
// a fallback + whenReady(key) swap (design decision G) — they never block tier 0. Only the
// LOCAL player's own body belongs here, and `needsFor` already adds it via `me` below.
// Most `icon_*` sprites are drawn by ONE specific scene (icon_lock: MainScene only;
// icon_hungry/icon_pet_heart: wherever AnimalsController can put a roaming pet), not by
// every scene, so they live on those scenes' own lists below, not here — a scene with a
// near-budget background (the street) shouldn't pay for icons it never draws. EVERYONE is
// the click sfx (decision F: "click w tier 0") plus `icon_home`, which no scene actually
// draws today (the nav chrome is text buttons) — it stays as the cheapest always-present
// key so tier 0 is never empty, and the manifest tests pin it as that anchor.
export const EVERYONE = ['icon_home', audioId('click')];
// Roaming pet (dog/cat) status bubbles — every scene AnimalsController can place one in.
const PET_ICONS = ['icon_hungry', 'icon_pet_heart'];
// The shared live sky's sprites (farm/skyLayer.js, backdropFx clouds) over the street and the yards.
const SKY_KEYS = ['farm_bd_sun', 'farm_bd_moon', ...[0, 1, 2, 3, 4].map((i) => `farm_bd_cloud_${i}`)];
// …and the pets themselves. AnimalSprite (ui/AnimalSprite.js) picks its texture once, at
// construction, with no whenReady swap, so a room that can host a roaming pet must block on
// the whole animal_* family (58 KB), not just the status bubbles — otherwise a cold cache
// leaves the pet a checkerboard for the entire visit. Any scene with PET_ICONS wants this.
const PET_ART = [...PET_ICONS, ...keysWith('animal_')];
// The farm's ground art: the endless lawn backdrop plus the plowed/fallow plot diamonds
// (FarmWorld.js). Needed by FarmScene/VisitFarmScene directly, and by HomeScene/
// VisitHomeScene too — the hall's door window (ui/FarmWindow.js) draws the live farm behind
// the glass right on the hall's first frame.
const FARM_ART = ['farm_lawn', 'farm_tile_plowed', 'farm_tile_fallow'];
// The scenery FarmWorld draws around the plots on its very first frame: the fence with its
// gate and posts, the dirt path out of the gate, and the deterministic lawn props. All of it
// together is well under 100 KB, and there is no fallback for any of it (a missing texture
// is simply skipped), so it blocks with the ground rather than popping in later.
// Only the first six props block (the meadow by the fence); the 20 later kinds (trees, forest) and
// the player's house come in tier 1 and paint in when they land (FarmWorld whenReady) — review
// 2026-09-30: with them all in tier 0 an empty level-5 farm blocked on 843 KB of the 1 MB cap.
const FARM_FIRST_PROPS = ['flowers_pink', 'flowers_yellow', 'tuft', 'rock', 'bush', 'tree'];
const FARM_SCENERY = ['farm_fence_se', 'farm_fence_sw', 'farm_gate',
  'farm_fence_post', 'farm_tile_path', ...FARM_FIRST_PROPS.map((k) => `farm_prop_${k}`)];
const FARM_LATER_SCENERY = (s) => [...FARM_PROP_KINDS.filter((k) => !FARM_FIRST_PROPS.includes(k)).map((k) => `farm_prop_${k}`),
  ...farmHouseKeys(s), ...farmPodworkoKeys(s)];
// Only what is planted right now: the distinct seeds in farm.cells, all three stages of each
// (a plot ripens without a scene reload, and the hall's window ticks through the stages too —
// FarmWorld.refresh()/_paintCrop). The whole 18-key family would push a level-5 hall over the
// 1 MB tier-0 cap; a fresh farm with nothing planted costs nothing beyond FARM_ART.
const farmCropKeys = (s) => [...new Set(Object.values(s?.farm?.cells ?? {}).filter((p) => p?.seed).map((p) => (p.c ? `${p.seed}_${p.c}` : p.seed)))]   // a flower colour: its own sprites (Kwiaty §7)
  .flatMap((seed) => FARM_CROP_STAGES.map((stage) => `farm_crop_${seed}_${stage}`));

// Only the vehicles the player owns (farm.tools size > 0) block the farm; the market fetches
// the rest when it opens.
// Only the objects placed on the farm block it; the market and the Schowek fetch the rest.
const farmObjectKeys = (s) => [...new Set((s?.placements ?? []).filter((p) => p.scene === 'farm').flatMap((p) => (
  p.type === 'fence' ? ['farm_obj_fence_post', 'farm_obj_fence_rail']
    : p.type === 'plaque' ? ['farm_obj_plaque']                    // its crop is an atlas icon (loaded at boot)
    : p.type === 'tree' ? FARM_TREE_STAGES.map((stage) => `farm_tree_${p.item}_${stage}`)
    : [`farm_obj_${p.item}`])))];

// The walk frames of the species out on the farm (a species without art yet draws its stand-in).
const farmAnimalKeys = (s) => [...new Set((s?.animals ?? []).filter((a) => a.scene === 'farm' && FARM_ANIMAL_IDS.includes(a.type)).map((a) => a.type))]
  .flatMap((id) => FARM_ANIMAL_POSES.map((pose) => `farm_animal_${id}_${pose}`));

// The player's house at the end of the path (farm/lawnProps.housePlacement): only its current level.
const farmHouseKeys = (s) => [`farm_house_lvl${Math.min(5, Math.max(1, Math.floor(s?.house?.level ?? 1)))}`];

// Podwórko beside the house (spec 2026-10-22): its level's picture and the garage cars, only when unlocked.
const farmPodworkoKeys = (s) => {
  const st = podworkoState(s);
  return st ? [`farm_podworko_lvl${st.level}`, ...new Set(st.cars.map((m) => `farm_car_${m}`))] : [];
};

// The machines you own, at their size — plus every machine's first size, which the tool strip shows before it is bought.
const farmVehicleKeys = (s) => [...new Set([...['tractor', 'seeder', 'harvester'].map((t) => `farm_vehicle_${t}_2`),
  ...Object.entries(s?.farm?.tools ?? {}).filter(([, n]) => n > 0).map(([t, n]) => `farm_vehicle_${t}_${n}`)])];

// Clothes pictures a player fetches: their own character's (shop / Garderoba art). Ken's (cloth_ken_*) are never
// body art — he wears walk sheets — so a Natalka player never needs them; Natalka's are body art only in flat-art
// mode (?anim=0, ctx.anim false: her peers are drawn with them), so a Ken player fetches them only then. (16 pictures
// are ~16 MB of texture memory on a phone.)
const ownClothes = (keys, character, anim = true) => keys.filter((k) => (k.startsWith('cloth_ken_')
  ? character === 'ken'
  : k.startsWith('cloth_nat_') ? character !== 'ken'   // Natalka's second wardrobe: shop art only, never on a body
    : character !== 'ken' || !anim));

function outfitKeys(equipped) {
  if (!equipped) return [];
  const out = [];
  for (const id of Object.values(equipped)) {
    if (!id || sheetOnly(id)) continue;
    out.push(`cloth_${id}`);
    if (exists(`cloth_${id}_back`)) out.push(`cloth_${id}_back`);
  }
  return out;
}

// A bed also brings its duvet front, drawn over whoever lies in it (FurnitureSeats).
const placementKeys = (state, area) => (state?.placements ?? [])
  .filter((p) => p.scene === area)
  .flatMap((p) => (p.type === 'furniture'
    ? [`furniture_${p.item}`, ...(exists(`furniture_${p.item}_front`) ? [`furniture_${p.item}_front`] : [])]
    : [p.type === 'decoration' ? `decoration_${p.item}` : `buda_${p.item}`]));

const ownedCars = (state, where, view) => (state?.cars?.owned ?? [])
  .filter((c) => (where ? c.location?.scene === where : true))
  .map((c) => `car_${c.model}_${view}`);
// A visit payload carries the host's garage cars flat in `cars` (street cars belong to the street).
// Guarded with Array.isArray: the `by` object literal below evaluates every scene's entry on
// every call (only the requested sceneKey's list is used), so this also runs against a plain
// player state whose `cars` is `{ owned: [...] }`, not an array.
const hostGarageCars = (host) => (Array.isArray(host?.cars) ? host.cars : []).map((c) => `car_${c.model}_front`);

// Bikes (the player's own, parked or ridden, a peer's, a visited host's) are layered riding atlases that BikeSprite
// loads on demand through bikeArt.js (bike plan 3, tasks 4 and 8): atlases need their JSON, which this image
// loader doesn't fetch, so none of them is a blocking key here.

const SHOP_STOCK = {
  ShopScene: [...keysWith('product_')],
  PetShopScene: [...keysWith('buda_'), ...keysWith('animal_'), ...keysWith('food_')],
  FurnitureShopScene: keysWith('furniture_').filter((k) => !k.endsWith('_front')),
  ClothingShopScene: keysWith('cloth_'),
};

// What a scene draws right now. `ctx.hostState` (visits) replaces `ctx.state` for the
// world being looked at; the visitor's own body/clothes still come from ctx.
export function needsFor(sceneKey, ctx) {
  const { phase = 'day', character = 'natalka', equipped = null, route = null } = ctx;
  const s = ctx.hostState ?? ctx.state ?? {};
  const me = [`${character}_base`, ...outfitKeys(equipped)];
  // The yards block on their current look and the live sky's sun, moon and clouds (farm/outdoorSky.js), as the street.
  const bgMain = yardKey('front', phase, s.house?.level ?? 1);
  const bgBack = yardRects('back', phase, s.backyard?.level ?? 1).map((r) => r.key);   // the far layer and the near picture
  const home = `bg_home_lvl${s.house?.level ?? 1}`;
  const homeBg = exists(home) ? home : 'bg_home';
  const by = {
    MainScene: [bgMain, ...SKY_KEYS, 'icon_lock', ...PET_ICONS, ...placementKeys(s, 'exterior'), ...keysWith('animal_')],
    VisitMainScene: [bgMain, ...SKY_KEYS, ...PET_ICONS, ...placementKeys(s, 'exterior'), ...keysWith('animal_')],
    // The instructor/mechanic/school-car/bikes are the NEIGHBOURing scenes' own business
    // (DrivingSchoolScene, CarDealerScene, BikeShopScene) and ride in via tier 1 — bg_street
    // alone (~950 KB) already fills almost the whole 1 MB budget (decision F), so this scene's
    // own list stays to just the background and whichever models are actually parked here.
    // Only the current look blocks (street/streetLooks.mjs); the sky behind it is the farm's (street/StreetSky.js):
    // its sun, moon and clouds are small and block too.
    OutsideScene: [...streetTileKeys(lookFor(phase)), ...SKY_KEYS, ...ownedCars(s, 'street', 'side'), 'car_yaris_l_side'],
    // The hall's door glass shows the live farm (ui/FarmWindow.js) right on the hall's first
    // frame — its ground art (FARM_ART) and whatever crops are actually planted must block too.
    // The hall's door window shows the max-level garden at this phase with the farm's crops in
    // its beds (ui/GardenWindow.js), so it needs that one background — not the farm's tiles.
    HomeScene: [homeBg, gardenBgKey(phase), ...PET_ART, ...placementKeys(s, 'home'), 'furniture_zegar_hand', ...farmCropKeys(s)],
    VisitHomeScene: [homeBg, gardenBgKey(phase), ...PET_ART, ...placementKeys(s, 'home'), 'furniture_zegar_hand', ...farmCropKeys(s)],
    KitchenScene: ['bg_kitchen', ...PET_ART, ...placementKeys(s, 'kitchen')],
    VisitKitchenScene: ['bg_kitchen', ...PET_ART, ...placementKeys(s, 'kitchen')],
    SalonScene: ['bg_salon', ...PET_ART, ...placementKeys(s, 'salon')],
    VisitSalonScene: ['bg_salon', ...PET_ART, ...placementKeys(s, 'salon')],
    SecondFloorScene: ['bg_second_floor', ...PET_ART, ...placementKeys(s, 'second_floor')],
    VisitSecondFloorScene: ['bg_second_floor', ...PET_ART, ...placementKeys(s, 'second_floor')],
    // The lawn backdrop and plot diamonds are real art (FARM_ART); so are the crops actually
    // planted right now (farmCropKeys) — an unplanted/undownloaded crop still shows the
    // tinted placeholder circle (farm/farmPlaceholders.js) until its art swaps in.
    // …plus the scenery around them and the painted backdrop (FARM_BACKDROP); the sky
    // itself is drawn in code (farm/FarmBackdrop.js).
    FarmScene: [...FARM_ART, ...FARM_SCENERY, ...FARM_BACKDROP, ...farmCropKeys(s), ...farmVehicleKeys(s), FARM_KONEWKA, FARM_SPLASH, ...farmObjectKeys(s), ...farmAnimalKeys(s)],
    // A friend's farm draws their objects and animals too (animals have no lazy load of their own).
    VisitFarmScene: [...FARM_ART, ...FARM_SCENERY, ...FARM_BACKDROP, ...farmCropKeys(s), FARM_KONEWKA, FARM_SPLASH, ...farmObjectKeys(s), ...farmAnimalKeys(s)],
    // The backyard's garage shows only the cars actually parked in it — see
    // BackyardScene.js's _garageCars() (filters location.scene === 'garage'); a car parked on
    // the street doesn't appear here (that's OutsideScene's business).
    BackyardScene: [...bgBack, ...SKY_KEYS, ...placementKeys(s, 'backyard'), ...ownedCars(s, 'garage', 'front')],
    VisitBackyardScene: [...bgBack, ...SKY_KEYS, ...placementKeys(s, 'backyard'), ...hostGarageCars(s)],
    ShopScene: ['bg_shop', ...SHOP_STOCK.ShopScene],
    PetShopScene: ['bg_pet_shop', ...SHOP_STOCK.PetShopScene],
    FurnitureShopScene: ['bg_furniture_shop', ...SHOP_STOCK.FurnitureShopScene],
    ClothingShopScene: ['bg_clothing_shop', ...ownClothes(SHOP_STOCK.ClothingShopScene, character, ctx.anim ?? true)],
    // The walk-through shop (bike plan 5): its two tiles. The bikes on display are riding atlases BikeSprite loads
    // on demand (see above); the old two-card shop's bg_bike_shop and bike_* pictures stay on disk, unused here.
    BikeShopScene: ['bg_bike_shop_0', 'bg_bike_shop_1'],
    GameHallScene: ['bg_game_hall_down', ...keysWith('game_'), 'ranking_board'],
    GameHallUpstairsScene: ['bg_game_hall_up', ...keysWith('game_')],
    // BoardGameScene.create(data) renders exactly one game's board (RENDERERS[data.game] ??
    // createCheckersRenderer — checkers is the fallback there too), never all three at once,
    // so only that family blocks; the rest ride NEIGHBOURS/tier 1 into GameHallScene instead.
    BoardGameScene: ['bg_board_game', ...keysWith(`${ctx.game ?? 'checkers'}_`)],
    // The theory quiz's illustrations (quiz_q*) are drawn by QuizModal.js, opened from the
    // instructor's menu — behind a modal, so they don't block the school's first frame.
    DrivingSchoolScene: ['bg_driving_school', 'npc_instructor'],
    CarDealerScene: ['bg_car_dealer', 'npc_mechanic', ...CAR_MODELS.map((m) => `car_${m}_front`)],
    // Which segments block depends on which lesson/exam route the child picked in the
    // driving-school menu — a choice made before this scene ever starts, so with no route
    // chosen yet (e.g. a NEIGHBOURS lookahead from DrivingSchoolScene) only the route-agnostic
    // actors block; ROUTE_DEFS.exam (9 segments, ~3.6 MB of backgrounds alone) would blow the
    // 1 MB cap on its own. Once a route is picked, its own segments/junctions do need to block.
    TrainingRouteScene: (() => {
      const segs = route ?? [];
      const junctions = segs.slice(0, -1).map((a, i) => `junction_${a}__${segs[i + 1]}`).filter(exists);
      return [...new Set(segs.map((id) => `bg_seg_${id}`)), ...junctions, 'car_yaris_l_side', `driver_${character}`,
        driverKey(character, ctx.look), driverKey(character, ctx.look, undefined, true),
        ...CAR_MODELS.map((m) => `car_${m}_side`), 'npc_instructor', 'npc_child', 'npc_child_walk', 'npc_child_walk_right',
        'sign_stop', 'sign_yield', 'sign_crossing', 'traffic_light_red', 'traffic_light_redyellow', 'traffic_light_green'];
    })(),
  };
  const keys = by[sceneKey];
  if (!keys) return [];
  return [...new Set([...EVERYONE, ...me, ...keys])].filter(exists);
}

// One step away by door, edge, stairs or menu — what tier 1 fetches so a walk never blocks.
export const NEIGHBOURS = {
  MainScene: ['OutsideScene', 'HomeScene', 'BackyardScene'],
  // FarmScene first: the hall shows the farm through the door glass right away.
  HomeScene: ['FarmScene', 'MainScene', 'KitchenScene', 'SalonScene', 'SecondFloorScene'],
  KitchenScene: ['HomeScene'], SalonScene: ['HomeScene'], SecondFloorScene: ['HomeScene'],
  FarmScene: ['HomeScene', 'BackyardScene'],
  BackyardScene: ['MainScene', 'FarmScene'],
  OutsideScene: ['MainScene', 'ShopScene', 'PetShopScene', 'FurnitureShopScene', 'ClothingShopScene', 'BikeShopScene',
    'GameHallScene', 'DrivingSchoolScene', 'CarDealerScene', 'BackyardScene'],
  ShopScene: ['OutsideScene'], PetShopScene: ['OutsideScene'], FurnitureShopScene: ['OutsideScene'],
  ClothingShopScene: ['OutsideScene'], BikeShopScene: ['OutsideScene'],
  GameHallScene: ['OutsideScene', 'GameHallUpstairsScene', 'BoardGameScene'],
  GameHallUpstairsScene: ['GameHallScene', 'BoardGameScene'], BoardGameScene: ['GameHallScene'],
  DrivingSchoolScene: ['OutsideScene', 'TrainingRouteScene'], CarDealerScene: ['OutsideScene'],
  TrainingRouteScene: ['OutsideScene'],
  VisitMainScene: ['VisitHomeScene', 'VisitBackyardScene'], VisitHomeScene: ['VisitFarmScene', 'VisitMainScene', 'VisitKitchenScene', 'VisitSalonScene', 'VisitSecondFloorScene'],
  VisitKitchenScene: ['VisitHomeScene'], VisitSalonScene: ['VisitHomeScene'], VisitSecondFloorScene: ['VisitHomeScene'],
  VisitFarmScene: ['VisitHomeScene', 'VisitBackyardScene'], VisitBackyardScene: ['VisitMainScene', 'VisitFarmScene'],
};

// The download order after tier 0: roughly how often a session visits each place, most first.
export const VISIT_ORDER = ['MainScene', 'HomeScene', 'OutsideScene', 'KitchenScene', 'SalonScene', 'SecondFloorScene',
  'ShopScene', 'PetShopScene', 'FurnitureShopScene', 'ClothingShopScene', 'BikeShopScene', 'FarmScene',
  'BackyardScene', 'GameHallScene', 'GameHallUpstairsScene', 'BoardGameScene',
  'DrivingSchoolScene', 'CarDealerScene', 'TrainingRouteScene'];

// The other three phases of the backgrounds this scene (or a neighbour) shows at MY level. The street's come
// in the order the day brings them; a phone (`lean`) gets none ahead — the street itself loads the next look
// shortly before the phase changes (street/streetLook.js), which keeps it under textureBudget's 180 MB.
function otherPhases(sceneKey, ctx, { lean = false } = {}) {
  if (sceneKey === 'OutsideScene') return lean ? [] : streetPrefetch(lookFor(ctx.phase));
  const s = ctx.hostState ?? ctx.state ?? {};
  const yard = { MainScene: 'front', VisitMainScene: 'front', BackyardScene: 'back', VisitBackyardScene: 'back' }[sceneKey];
  if (!yard) return [];
  return yardPrefetch(yard, ctx.phase, yard === 'front' ? s.house?.level ?? 1 : s.backyard?.level ?? 1, { lean });
}

// Every key that is a level variant of some other level than mine (never prefetched).
function otherLevelBackgrounds(ctx) {
  const s = ctx.hostState ?? ctx.state ?? {};
  const mine = new Set([...YARD_LOOKS.map((l) => yardKey('front', l, s.house?.level ?? 1)),
    ...YARD_LOOKS.map((l) => yardKey('back', l, s.backyard?.level ?? 1)),
    `bg_home_lvl${s.house?.level ?? 1}`]);
  // Every level-variant background family; anything in it that is not MY level stays out.
  return ALL_KEYS.filter((k) => /^bg_(home|yard)/.test(k) && !mine.has(k));
}

// `lean` (phones, textureBudget.mjs): the places next door come as they look NOW — not their
// other phases (6–9 MB pictures each) — and without the wardrobe's pictures, which only the
// Garderoba and the clothes shop draw (the shop blocks on them; the Garderoba swaps them in),
// nor the furniture shop's stock (fourteen sofas and beds, ~16 MB decoded; the shop blocks on it).
const FURNITURE_STOCK = new Set(SHOP_STOCK.FurnitureShopScene);
export function tiers(sceneKey, ctx, { lean = false } = {}) {
  const t0 = needsFor(sceneKey, ctx);
  const seen = new Set(t0);
  const take = (keys) => keys.filter((k) => exists(k) && !seen.has(k) && (seen.add(k), true));
  const t1 = take([
    // the music right after the scene itself (0.35 MB): last in line, a phone reached it minutes in
    // or never, while big art went first (owner 2026-10-18: "on mobile there's no music, ever")
    audioId(MUSIC_KEY),
    ...keysWith('icon_'),
    ...(lean ? [] : ownClothes(keysWith('cloth_'), ctx?.character ?? 'natalka', ctx?.anim ?? true)), 'natalka', 'natalka_base', 'ken_base',
    ...otherPhases(sceneKey, ctx, { lean }),
    ...(sceneKey === 'FarmScene' || sceneKey === 'VisitFarmScene' ? FARM_LATER_SCENERY(sceneKey === 'VisitFarmScene' ? (ctx?.hostState ?? ctx?.state) : ctx?.state) : []),
    ...(NEIGHBOURS[sceneKey] ?? []).flatMap((n) => (lean
      // (nor the Podwórko's far landscape: a 50 KB download that costs a phone 2 MB decoded; it loads on the way in)
      ? needsFor(n, ctx).filter((k) => !k.startsWith('cloth_') && !(n === 'FurnitureShopScene' && FURNITURE_STOCK.has(k))
        && !k.startsWith('bg_yard_back_far_'))
      : [...needsFor(n, ctx), ...otherPhases(n, ctx)])),
  ]);
  // a style's car picture only for the player who wears it: a car loads its riders' when it shows them (CarSprite)
  const own = [false, true].map((right) => driverKey(ctx?.character ?? 'natalka', ctx?.look, undefined, right));
  const skip = new Set([...otherLevelBackgrounds(ctx), ...keysWith('cloth_').filter((k) => !ownClothes([k], ctx?.character ?? 'natalka', ctx?.anim ?? true).length),
    ...driverPictures().filter((k) => !own.includes(k))]);
  // Route art is in the manifest so TrainingRouteScene can load it, but it is never
  // prefetched: ~7.5 MB that only a driving lesson looks at (see the IMAGE_FOLDERS note).
  const isRouteArt = (k) => k.startsWith('bg_seg_') || k.startsWith('junction_');
  const rest = VISIT_ORDER.flatMap((s) => [...needsFor(s, ctx), ...otherPhases(s, ctx)]);
  const t2 = take([
    ...rest,
    ...ALL_KEYS.filter((k) => ASSETS[k].kind === 'image' && !skip.has(k) && !isRouteArt(k)),
    ...SFX_KEYS.map(audioId),
  ]);
  return [t0, t1, t2];
}
